import React, { useState } from 'react';
import styled from 'styled-components';
import { Paciente } from '../types';

const FormContainer = styled.div`
  margin: 20px 0;
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h2`
  color: #0078d4;
  font-size: 1.8rem;
  margin-bottom: 25px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 20px;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 10px;
  color: #333;
  display: flex;
  align-items: center;
  
  &::after {
    content: '*';
    color: #d32f2f;
    margin-left: 4px;
    font-size: 1.2em;
  }
`;

const Input = styled.input`
  padding: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #0078d4;
    box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.2);
    transform: translateY(-2px);
  }
  
  &:invalid {
    border-color: #d32f2f;
  }
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-size: 0.875rem;
  margin-top: 5px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SubmitButton = styled.button`
  grid-column: 1 / -1;
  background-color: #0078d4;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 8px rgba(0, 120, 212, 0.2);
  
  &:hover {
    background-color: #005a9e;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 120, 212, 0.3);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 14px;
  }
`;

const Icon = styled.span`
  font-size: 1.2rem;
`;

const PrivacyText = styled.p`
  grid-column: 1 / -1;
  font-size: 0.9rem;
  color: #666;
  margin-top: 20px;
  text-align: center;
  line-height: 1.5;
`;

interface FormularioPacienteProps {
  onSubmit: (paciente: Paciente) => void;
  isSubmitting?: boolean;
}

const FormularioPaciente: React.FC<FormularioPacienteProps> = ({
  onSubmit,
  isSubmitting = false
}) => {
  const [formData, setFormData] = useState<Paciente>({
    nombre: '',
    apellidos: '',
    email: '',
    telefono: ''
  });
  
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validarCampo = (name: string, value: string): string => {
    switch (name) {
      case 'nombre':
      case 'apellidos':
        return value.trim() === '' ? 'Este campo es obligatorio' : '';
      case 'email':
        return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) 
          ? 'Introduce un email válido' 
          : '';
      case 'telefono':
        return !/^\d{9}$/.test(value) 
          ? 'Introduce un número de teléfono válido (9 dígitos)' 
          : '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validar en tiempo real
    const error = validarCampo(name, value);
    setErrores(prev => ({ ...prev, [name]: error }));
  };
  
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar todos los campos antes de enviar
    const nuevosErrores: Record<string, string> = {};
    let formValido = true;
    
    Object.entries(formData).forEach(([key, value]) => {
      const error = validarCampo(key, value as string);
      if (error) {
        nuevosErrores[key] = error;
        formValido = false;
      }
    });
    
    setErrores(nuevosErrores);
    setTouched({
      nombre: true,
      apellidos: true,
      email: true,
      telefono: true
    });
    
    if (formValido) {
      onSubmit(formData);
    }
  };

  return (
    <FormContainer>
      <Title>Tus Datos</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="nombre">Nombre</Label>
          <Input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="given-name"
          />
          {touched.nombre && errores.nombre && <ErrorText>{errores.nombre}</ErrorText>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="apellidos">Apellidos</Label>
          <Input
            type="text"
            id="apellidos"
            name="apellidos"
            value={formData.apellidos}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="family-name"
          />
          {touched.apellidos && errores.apellidos && <ErrorText>{errores.apellidos}</ErrorText>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="email"
          />
          {touched.email && errores.email && <ErrorText>{errores.email}</ErrorText>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="telefono">Teléfono</Label>
          <Input
            type="tel"
            id="telefono"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            autoComplete="tel"
            placeholder="Ej: 666123456"
          />
          {touched.telefono && errores.telefono && <ErrorText>{errores.telefono}</ErrorText>}
        </FormGroup>
        
        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>Procesando... <Icon>⏳</Icon></>
          ) : (
            <>Continuar al Pago <Icon>→</Icon></>
          )}
        </SubmitButton>
        
        <PrivacyText>
          Al continuar, aceptas nuestra política de privacidad y términos de servicio. 
          Tus datos serán tratados con confidencialidad y solo se utilizarán para gestionar tu cita.
        </PrivacyText>
      </Form>
    </FormContainer>
  );
};

export default FormularioPaciente; 